/**
 * ContextGroup Component
 *
 * Wraps a context with its associated questions.
 * Supports both sample contexts (read-only) and generated contexts (with edit controls).
 *
 * @module ContextGroup
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, X, Edit, Trash2, Volume2 } from 'lucide-react';
import ContextContentDisplay from './ContextContentDisplay';
import QuestionCard from './QuestionCard';
import { fetchContextBinaryContent } from '@/lib/contextApi';

interface ContextGroupProps {
  readonly contextName: string;
  readonly questions: any[];
  readonly variant: 'sample' | 'generated';
  readonly editingContext?: string | null;
  readonly editingContextData?: { name: string; content: string } | null;
  readonly onEditContext?: () => void;
  readonly onSaveContext?: () => void;
  readonly onCancelEditContext?: () => void;
  readonly onSaveContextToDatabase?: () => void;
  readonly onGenerateAudio?: () => void;
  readonly onDeleteContext?: () => void;
  readonly generatingAudio?: boolean;
  readonly editingQuestion?: string | null;
  readonly onEditQuestion?: (questionId: string) => void;
  readonly onSaveQuestion?: () => void;
  readonly onCancelEditQuestion?: () => void;
  readonly onDeleteQuestion?: (questionId: string) => void;
  readonly onContextNameChange?: (name: string) => void;
  readonly onContextContentChange?: (content: string) => void;
  readonly editingQuestionData?: any;
  readonly onQuestionTextChange?: (text: string) => void;
  readonly onOptionsChange?: (options: string[]) => void;
  readonly onExpectedAnswerChange?: (answer: string | string[]) => void;
  readonly onExplanationChange?: (explanation: string) => void;
  readonly skills?: string[];
}

/**
 * Component that displays a context group with its questions.
 * Handles both sample contexts (simpler) and generated contexts (with edit controls).
 *
 * @param contextName - Name of the context
 * @param questions - Array of questions in this context
 * @param variant - Type of context group ('sample' or 'generated')
 * @param editingContext - Currently editing context name (for generated)
 * @param editingContextData - Context data being edited
 * @param onEditContext - Callback to start editing context
 * @param onSaveContext - Callback to save context edits
 * @param onCancelEditContext - Callback to cancel context edits
 * @param onSaveContextToDatabase - Callback to save context to database
 * @param onGenerateAudio - Callback to generate audio for context
 * @param onDeleteContext - Callback to delete context
 * @param generatingAudio - Whether audio is being generated
 * @param editingQuestion - Currently editing question ID
 * @param onEditQuestion - Callback to edit a question
 * @param onSaveQuestion - Callback to save question edits
 * @param onCancelEditQuestion - Callback to cancel question edits
 * @param onDeleteQuestion - Callback to delete a question
 * @param onContextNameChange - Callback when context name changes in edit mode
 * @param onContextContentChange - Callback when context content changes in edit mode
 */
export default function ContextGroup({
  contextName,
  questions,
  variant,
  editingContext,
  editingContextData,
  onEditContext,
  onSaveContext,
  onCancelEditContext,
  onSaveContextToDatabase,
  onGenerateAudio,
  onDeleteContext,
  generatingAudio = false,
  editingQuestion,
  onEditQuestion,
  onSaveQuestion,
  onCancelEditQuestion,
  onDeleteQuestion,
  onContextNameChange,
  onContextContentChange,
  editingQuestionData,
  onQuestionTextChange,
  onOptionsChange,
  onExpectedAnswerChange,
  onExplanationChange,
  skills,
}: ContextGroupProps) {
  const firstQuestion = questions[0];
  const questionCount = questions.length;
  const isEditing = editingContext === contextName;
  const isAudioContext = firstQuestion?.context_type === 'audio';
  const hasAudio = !!firstQuestion?.binary_content_base64;
  const generationModel =
    firstQuestion?.generationModel ||
    firstQuestion?.generation_model ||
    firstQuestion?.context?.generationModel ||
    firstQuestion?.context?.generation_model;

  // Get skills from prop or from first question's context_skills
  const skillsToShow = skills || firstQuestion?.context_skills || [];

  // Check if context has similarity (check first question as questions are tagged with similarity info)
  const hasSimilarity = firstQuestion?.hasSimilarity === true;
  const similarityPercentage = firstQuestion?.similarityPercentage;

  // Determine container styling based on variant
  const containerClassName =
    variant === 'sample'
      ? 'p-4 bg-muted rounded-lg border border-border'
      : 'p-4 bg-primary/10 rounded-lg border border-primary/20';

  const headerTextColor =
    variant === 'sample' ? 'text-foreground' : 'text-primary';

  // Context name display/edit
  const renderContextName = () => {
    if (variant === 'generated' && isEditing && onContextNameChange) {
      return (
        <Input
          value={editingContextData?.name || ''}
          onChange={(e) => onContextNameChange(e.target.value)}
          className="w-64"
        />
      );
    }

    const prefix = variant === 'generated' ? 'Generated Context: ' : 'Context: ';
    return `${prefix}${contextName}`;
  };

  // Context header controls (only for generated contexts)
  const renderContextControls = () => {
    if (variant !== 'generated') return null;

    if (isEditing) {
      return (
        <>
          <Button size="sm" variant="outline" onClick={onSaveContext}>
            <Save className="h-3 w-3" />
          </Button>
          <Button size="sm" variant="outline" onClick={onCancelEditContext}>
            <X className="h-3 w-3" />
          </Button>
        </>
      );
    }

    return (
      <>
        <Button
          size="sm"
          variant="outline"
          onClick={onSaveContextToDatabase}
          disabled={isAudioContext && !hasAudio}
          title={
            isAudioContext && !hasAudio
              ? 'Generate audio first'
              : 'Save to database'
          }
        >
          <Save className="h-3 w-3" />
        </Button>
        {isAudioContext && onGenerateAudio && (
          <Button
            size="sm"
            variant="outline"
            onClick={onGenerateAudio}
            disabled={generatingAudio}
            className="text-primary hover:text-primary/80"
            title={hasAudio ? 'Regenerate audio' : 'Generate audio'}
          >
            <Volume2 className="h-3 w-3" />
          </Button>
        )}
        {onEditContext && (
          <Button size="sm" variant="outline" onClick={onEditContext}>
            <Edit className="h-3 w-3" />
          </Button>
        )}
        {onDeleteContext && (
          <Button size="sm" variant="outline" onClick={onDeleteContext}>
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </>
    );
  };

  return (
    <div className="space-y-3">
      {/* Context Header and Content */}
      <div className={containerClassName}>
        <div
          className={`flex items-center ${variant === 'generated' ? 'justify-between' : 'gap-2'} mb-3`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <h4 className={`font-medium ${headerTextColor} font-ui`}>
              {renderContextName()}
            </h4>
            <Badge variant="outline" className="font-ui">
              {questionCount} {questionCount > 1 ? 'questions' : 'question'}
            </Badge>
            {variant === 'generated' && (
              <Badge className="bg-primary/10 text-primary border-primary/20 font-ui">
                AI Generated
              </Badge>
            )}
            {variant === 'generated' && generationModel && (
              <Badge variant="outline" className="font-ui text-xs">
                Model: {generationModel}
              </Badge>
            )}
            {hasSimilarity && similarityPercentage !== undefined && (
              <Badge className="bg-muted text-foreground border-border font-ui">
                Similar ({similarityPercentage}%)
              </Badge>
            )}
            {skillsToShow.length > 0 && skillsToShow.map((skill: string) => (
              <Badge
                key={skill}
                variant="secondary"
                className="font-ui text-xs"
              >
                {skill}
              </Badge>
            ))}
          </div>

          {variant === 'generated' && (
            <div className="flex items-center gap-1">
              {renderContextControls()}
            </div>
          )}
        </div>

        {/* Context Content */}
        {/* Show context section if any context data exists: context_id, context_content, transcript, or binary_content_base64 */}
        {(firstQuestion?.context_id ||
          firstQuestion?.context_content ||
          firstQuestion?.transcript ||
          firstQuestion?.binary_content_base64) && (
            <div className="mt-3 p-3 bg-background rounded border border-border">
              <h5 className="text-sm font-medium text-foreground mb-2 font-ui">
                Context Content
              </h5>
              <ContextContentDisplay
                contextType={firstQuestion.context_type}
                transcript={firstQuestion.transcript}
                audioBase64={firstQuestion.binary_content_base64}
                mimeType={firstQuestion.mime_type}
                contextContent={firstQuestion.context_content}
                isEditing={isEditing}
                editingContent={editingContextData?.content}
                onContentChange={onContextContentChange}
                contextId={firstQuestion.context_id}
                fetchBinaryContent={fetchContextBinaryContent}
              />
            </div>
          )}
      </div>

      {/* Questions in this context */}
      <div className="space-y-3 ml-4">
        {questions.map((question: any, questionIndex: number) => {
          // Generate questionId matching the format used in handleEditQuestion
          const questionId =
            question.id ||
            (question.context_name &&
              question.text &&
              `${question.context_name}-${question.text.substring(0, 20)}`) ||
            (variant === 'generated'
              ? `${contextName}-${question.originalIndex}`
              : `${contextName}-${questionIndex}`);

          return (
            <QuestionCard
              key={questionId}
              question={question}
              index={question.originalIndex}
              variant={variant}
              isEditing={editingQuestion === questionId}
              contextName={contextName}
              onEdit={
                onEditQuestion ? () => onEditQuestion(questionId) : undefined
              }
              onDelete={
                onDeleteQuestion
                  ? () => onDeleteQuestion(questionId)
                  : undefined
              }
              onSave={onSaveQuestion}
              onCancel={onCancelEditQuestion}
              editingQuestionData={
                editingQuestion === questionId ? editingQuestionData : undefined
              }
              onQuestionTextChange={onQuestionTextChange}
              onOptionsChange={onOptionsChange}
              onExpectedAnswerChange={onExpectedAnswerChange}
              onExplanationChange={onExplanationChange}
            />
          );
        })}
      </div>
    </div>
  );
}
